import { supabase } from '../supabaseClient';
import { slugify } from '../utils/subdomain';
import { devError } from '../utils/devLog';

// Fetch dynamic resume links by role
export const fetchResumeLinks = async (role) => {
  const { data, error } = await supabase
    .from('resumes')
    .select('preview_url, download_url')
    .eq('role', role)
    .single();

  if (error) {
    if (error.code !== 'PGRST116') {
      devError(`Error fetching resume for role ${role}:`, error);
    }
    return null;
  }
  return data;
};

// Fetch all projects
export const fetchProjects = async () => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Supabase fetchProjects Error:', error);
    devError('Error fetching projects:', error);
    return [];
  }
  console.log('Supabase fetchProjects Success:', data);
  return data;
};

// Fetch project by ID
export const fetchProjectById = async (id) => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    devError('Error fetching project:', error);
    return null;
  }
  return data;
};

// Fetch project by slug derived from its title (e.g. "sowbeez").
// Matches on a slugified title so URLs stay stable even with spaces/punctuation.
export const fetchProjectBySlug = async (slug) => {
  const { data, error } = await supabase
    .from('projects')
    .select('*');

  if (error) {
    devError('Error fetching project by slug:', error);
    return null;
  }

  const target = slugify(slug);
  return (data || []).find((p) => slugify(p.title) === target) || null;
};

// Fetch project likes count
export const fetchProjectLikes = async (projectId) => {
  const { count, error } = await supabase
    .from('project_likes')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId);

  if (error) {
    devError('Error fetching likes:', error);
    return 0;
  }
  return count || 0;
};

// Stable per-browser identifier used to attribute likes. Despite the legacy
// "user_ip" column name, this is a random UUID, not a real IP address.
const getSiteUserId = () => {
  let userId = localStorage.getItem('site_user_id');
  if (!userId) {
    userId = crypto.randomUUID();
    localStorage.setItem('site_user_id', userId);
  }
  return userId;
};

// Check whether the current browser has already liked a project
export const hasUserLikedProject = async (projectId) => {
  const userId = localStorage.getItem('site_user_id');
  if (!userId) return false;
  const { count, error } = await supabase
    .from('project_likes')
    .select('*', { count: 'exact', head: true })
    .eq('project_id', projectId)
    .eq('user_ip', userId);
  if (error) {
    devError('Error checking like status:', error);
    return false;
  }
  return (count || 0) > 0;
};

// Toggle project like
export const toggleProjectLike = async (projectId, isLiked) => {
  const userId = getSiteUserId();

  if (isLiked) {
    const { error } = await supabase
      .from('project_likes')
      .delete()
      .eq('project_id', projectId)
      .eq('user_ip', userId);
    return !error;
  } else {
    // Guard against duplicate inserts if the UI state was stale
    const alreadyLiked = await hasUserLikedProject(projectId);
    if (alreadyLiked) return true;
    const { error } = await supabase
      .from('project_likes')
      .insert([{ project_id: projectId, user_ip: userId }]);
    return !error;
  }
};

// Fetch project feedbacks
export const fetchProjectFeedbacks = async (projectId) => {
  const { data, error } = await supabase
    .from('project_feedback')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    devError('Error fetching feedbacks:', error);
    return [];
  }
  return data || [];
};

// Submit project feedback
export const submitProjectFeedback = async (projectId, { name, email, message, rating }) => {
  if (!projectId || !name?.trim() || !message?.trim()) {
    devError('Validation error: projectId, name, and message are required');
    return false;
  }
  if (name.length > 100) {
    devError('Validation error: name too long (max 100 chars)');
    return false;
  }
  if (message.length > 2000) {
    devError('Validation error: message too long (max 2000 chars)');
    return false;
  }
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    devError('Validation error: invalid email format');
    return false;
  }
  const safeRating = Math.min(5, Math.max(1, Math.floor(Number(rating) || 5)));

  const { error } = await supabase
    .from('project_feedback')
    .insert([{ project_id: projectId, name: name.trim(), email: email?.trim() || null, message: message.trim(), rating: safeRating }]);

  if (error) {
    devError('Error submitting feedback:', error);
    return false;
  }
  return true;
};

// Fetch testimonials
export const fetchTestimonials = async () => {
  const { data, error } = await supabase
    .from('testimonials')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Supabase fetchTestimonials Error:', error);
    devError('Error fetching testimonials:', error);
    return [];
  }
  console.log('Supabase fetchTestimonials Success:', data);
  // Map title -> author, description -> text to match frontend expectations
  return (data || []).map(t => ({
    ...t,
    author: t.author || t.title,
    text: t.text || t.description
  }));
};

// Fetch status
export const fetchStatus = async () => {
  const { data, error } = await supabase
    .from('statuses')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    devError('Error fetching status:', error);
    return null;
  }
  return data?.status;
};

// Fetch service packages
export const fetchServicePackages = async () => {
  const { data, error } = await supabase
    .from('service_packages')
    .select('*')
    .order('id', { ascending: true });

  if (error) {
    devError('Error fetching service packages:', error);
    return [];
  }
  return data || [];
};

// Fetch qualification education
export const fetchEducation = async () => {
  const { data, error } = await supabase
    .from('qualification_education')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    devError('Error fetching education:', error);
    return [];
  }
  return data || [];
};

// Fetch qualification experience
export const fetchExperience = async () => {
  const { data, error } = await supabase
    .from('qualification_experience')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    devError('Error fetching experience:', error);
    return [];
  }
  return data || [];
};

// Fetch chatbot FAQs
export const fetchChatbotFAQs = async () => {
  const { data, error } = await supabase
    .from('chatbot_faqs')
    .select('*');

  if (error) {
    devError('Error fetching chatbot FAQs:', error);
    return [];
  }
  return data || [];
};